import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

const ImageZoom = ({ src, alt = "Zoomed Image" }) => {
  const [isOpen, setIsOpen] = useState(false); // For Modal
  const [isHovering, setIsHovering] = useState(false); // For Inline Zoom
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });

  // Modal State
  const [modalZoomed, setModalZoomed] = useState(false);
  const [modalDimensions, setModalDimensions] = useState({ width: 0, height: 0 });
  const [modalCursor, setModalCursor] = useState({ x: 0, y: 0 });

  const imgRef = useRef(null);
  const modalImgRef = useRef(null);
  const lensRef = useRef(null);

  const ZOOM_LEVEL = 4;
  const LENS_SIZE = 100;

  // --- Inline Hover Handlers ---
  const handleMouseEnter = () => setIsHovering(true);
  const handleMouseLeave = () => setIsHovering(false);

  const handleMouseMove = (e) => {
    if (!imgRef.current) return;

    const { left, top, width, height } = imgRef.current.getBoundingClientRect();

    // Calculate Portal Position (Right of the image)
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;

    if (isHovering) {
      setPortalPosition({
        top: top + scrollY,
        left: left + width + 20 + scrollX, // 20px gap
        height: height,
      });
    }

    let x = e.clientX - left;
    let y = e.clientY - top;

    // Ensure x/y are within bounds for the cursor tracker
    x = Math.max(0, Math.min(x, width));
    y = Math.max(0, Math.min(y, height));

    setCursorPosition({ x, y, width, height });
  };


  // --- Modal Handlers ---
  const handleModalClose = () => {
    setIsOpen(false);
    setModalZoomed(false);
  };

  const toggleModalZoom = (e) => {
    e.stopPropagation();
    setModalZoomed(!modalZoomed);
  };

  const handleModalMouseMove = (e) => {
    if (!modalImgRef.current || !modalZoomed) return;

    const { left, top, width, height } = modalImgRef.current.getBoundingClientRect();
    if (width !== modalDimensions.width || height !== modalDimensions.height) {
      setModalDimensions({ width, height });
    }

    let x = e.clientX - left;
    let y = e.clientY - top;
    x = Math.max(0, Math.min(x, width));
    y = Math.max(0, Math.min(y, height));

    setModalCursor({ x, y });
  }


  return (
    <>
      <div
        className="cursor-pointer overflow-hidden rounded-lg group h-full w-full relative"
        onClick={() => setIsOpen(true)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        ref={imgRef}
      >
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

        {/* Inline Lens */}
        {isHovering && !isOpen && (
          <div
            ref={lensRef}
            className="absolute border border-brand-500 bg-white/20 pointer-events-none shadow-sm z-10"
            style={{
              width: `${LENS_SIZE}px`,
              height: `${LENS_SIZE}px`,
              left: `${cursorPosition.x - LENS_SIZE / 2}px`,
              top: `${cursorPosition.y - LENS_SIZE / 2}px`,
            }}
          />
        )}
      </div>

      {/* Inline Zoom Flyout (Portal) */}
      {isHovering && !isOpen && createPortal(
        <div
          className="fixed z-[9999] bg-white border border-gray-200 shadow-2xl overflow-hidden rounded-lg pointer-events-none hidden lg:block"
          style={{
            top: portalPosition.top - 100,
            left: portalPosition.left,
            width: '500px',
            height: '500px',
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${(cursorPosition.width || 0) * ZOOM_LEVEL}px ${(cursorPosition.height || 0) * ZOOM_LEVEL}px`,
            backgroundPositionX: `-${(cursorPosition.x * ZOOM_LEVEL) - 250}px`, // 250 = half flyout width
            backgroundPositionY: `-${(cursorPosition.y * ZOOM_LEVEL) - 250}px`
          }}
        >
        </div>,
        document.body
      )}


      {/* Full Modal (Gallery View) */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[99999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={handleModalClose}
        >
          <button
            className="absolute top-5 right-5 text-white/70 hover:text-white z-50 p-2"
            onClick={(e) => {
              e.stopPropagation();
              handleModalClose();
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div
            className={`flex items-center justify-center w-full h-full overflow-hidden ${modalZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
            onMouseMove={handleModalMouseMove}
          >
            {/* Modal Image Logic (Click to Zoom in Place) */}
            {!modalZoomed ? (
              <img
                src={src}
                alt={alt}
                className="max-h-[90vh] max-w-[90vw] object-contain transition-transform duration-300"
                onClick={toggleModalZoom}
              />
            ) : (
              // Zoomed State in Modal (Pan around)
              <div
                className="w-full h-full overflow-hidden relative"
                onClick={toggleModalZoom}
              >
                <div
                  className="absolute inset-0 bg-black"
                  onMouseMove={(e) => {
                    const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - left) / width * 100;
                    const y = (e.clientY - top) / height * 100;
                    e.currentTarget.style.backgroundPosition = `${x}% ${y}%`;
                  }}
                  style={{
                    backgroundImage: `url(${src})`,
                    backgroundSize: '250%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    cursor: 'zoom-out'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ImageZoom;
